/**
 * Rooms under a Concept Level — shared by the Reference > Design > Concepts
 * branch (DesignSection.jsx, via ConceptLevelsSection.jsx) and the DAPC Drawings
 * milestone Design node (MilestoneSections.jsx). Self-contained: given a levelId
 * it fetches, renders, and mutates its own room list via services/design.js, and
 * mirrors every change into ctx.roomCache so the vanilla outline engine's
 * breadcrumb/label-resolution code (which walks that cache synchronously) can
 * resolve a room's name for the room-detail zoom's breadcrumb/title.
 *
 * Renders in two modes — tree (default, full header) vs zoomMode (children only,
 * used for sec:level:<levelId>:rooms; "Add Room" reaches this component via the
 * 'open-add-room' CustomEvent, detail { levelId }).
 *
 * Each room is itself expandable, showing its 4 settings (Room Name, Room Template,
 * Scope Type, Room Number) plus a "Room Area" branch as child rows — the SAME
 * `RoomFieldsList` renders identically in the inline tree AND in the room's own room-detail
 * zoom content (DesignSection.jsx), so both surfaces show and edit the same fields the same
 * way (one canonical renderer, per the app's node-type convention). Each field row has its own
 * ⋯ menu ("Edit") opening a single-field modal; the room's own row keeps a top-level "Edit"
 * (the full RoomFormModal, useful for filling in every field on a legacy room in one pass) and
 * "Delete". Deleting the room stays room-level only — never offered on a field row.
 *
 * "Room Area" groups the two ways area gets entered — Length × Width (the conceptual estimate,
 * available from day one even with nothing sketched yet) and a Manual Area Override (the real
 * number once a takeoff or as-built measurement exists). Airtable's Room Area Net formula
 * already gives Area Manual priority when set; this branch is only the missing UI for both
 * inputs, showing the live computed net SF Airtable resolves. This is also what feeds the
 * Control Budget / Budget Explorer section on the CF and IDP presentations (BudgetExplorer.jsx)
 * — it renders nothing at all if every room on the concept has 0 net SF, with no visible
 * explanation, so a project with no room areas entered will silently show no Control Budget
 * section on either presentation.
 */
import { useState, useEffect, useRef } from 'react'
import { wf } from '../outline/state.js'
import { ctx } from '../outline/ctx.js'
import { useActionMenu, ActionMenu, ActionMenuButton, ActionMenuItem } from './ActionMenu.jsx'
import { confirmTypeDialog } from './Dialog.jsx'
import { getLevelRooms, createRoom, updateRoom, deleteRoom, getRoomTemplates } from '../services/design.js'
import { SCOPE_TYPE_OPTIONS, SCOPE_TYPE_DEFINITIONS } from '../outline/constants.js'
import SearchablePicker from './SearchablePicker.jsx'

function roomDisplayName(room) {
  return room.fields['Room'] || room.fields['Room Number and Name'] || 'Room'
}

/** Room Templates barely change — fetch once per page load, shared by every Add/Edit modal.
 *  Exported so other surfaces (e.g. the Program Model tool's Add Room / room info dialogs)
 *  reuse the exact same cache and picker instead of re-fetching. */
export function useRoomTemplates() {
  const [templates, setTemplates] = useState(ctx.roomTemplatesCache || null)
  useEffect(() => {
    if (ctx.roomTemplatesCache) return
    getRoomTemplates()
      .then(list => { ctx.roomTemplatesCache = list; setTemplates(list) })
      .catch(() => setTemplates([]))
  }, [])
  return templates
}

function templateName(templates, id) {
  if (!id) return ''
  const t = (templates || []).find(x => x.id === id)
  return t ? t.name : ''
}

/** Searchable Room Template combobox — ~60 templates is too many to scan in a plain <select>.
 *  Thin wrapper over the shared SearchablePicker (same combobox used by Control Budget Model). */
function RoomTemplatePicker({ templates, value, onChange, inputStyle }) {
  const items = templates ? templates.map(t => ({ id: t.id, label: t.name })) : null
  return <SearchablePicker items={items} value={value} onChange={onChange} placeholder="Search templates…" inputStyle={inputStyle} />
}

/* ── Full add/edit form (all 4 fields at once) — used for Add, and kept as a "fix up
   everything" option on the room's own ⋯ menu alongside the per-field edits below. Exported so
   other surfaces (e.g. the Program Model tool) can add a room with the exact same required
   fields as the outline, rather than a parallel form. ── */
export function RoomFormModal({ title, initial, onSave, onClose }) {
  const [roomNumber, setRoomNumber] = useState(initial?.['Room Number'] || '')
  const [customName, setCustomName] = useState(initial?.['Custom Name'] || '')
  const [scopeType, setScopeType] = useState(initial?.['Scope Type'] || '')
  const [roomTemplateId, setRoomTemplateId] = useState((initial?.['Room Template'] || [])[0] || '')
  const [saving, setSaving] = useState(false)
  const [error, setError] = useState('')
  const templates = useRoomTemplates()

  const inp = {
    fontFamily: '"DM Sans",sans-serif', fontSize: 12,
    padding: '8px 10px', border: '0.5px solid var(--aj-border-mid)',
    borderRadius: 4, background: 'var(--aj-bg-subtle)', color: 'var(--aj-text)',
    outline: 'none', boxSizing: 'border-box', width: '100%',
  }

  function handleTemplateChange(id) {
    setRoomTemplateId(id)
    // Convenience: adopt the template's name automatically only while the name field is still
    // empty — once the user has typed anything, switching templates never clobbers it.
    if (!customName.trim()) {
      const tpl = (templates || []).find(t => t.id === id)
      if (tpl) setCustomName(tpl.name)
    }
  }
  function adoptTemplateName() {
    const tpl = (templates || []).find(t => t.id === roomTemplateId)
    if (tpl) setCustomName(tpl.name)
  }

  const missingName = !customName.trim()
  const missingScope = !scopeType
  const missingTemplate = !roomTemplateId
  const disabled = missingName || missingScope || missingTemplate

  async function handleSave() {
    if (disabled || saving) return
    setSaving(true)
    setError('')
    try {
      await onSave({
        'Room Number': roomNumber.trim(),
        'Custom Name': customName.trim(),
        'Scope Type': scopeType,
        'Room Template': [roomTemplateId],
      })
      onClose()
    } catch (err) {
      setSaving(false)
      setError(err.message || 'Could not save this room.')
    }
  }

  return (
    <div
      style={{ position: 'fixed', inset: 0, zIndex: 900, background: 'rgba(0,0,0,0.35)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}
      onClick={onClose}
    >
      <div
        style={{ background: 'var(--aj-bg-elevated)', borderRadius: 10, padding: '28px 32px', width: 380, maxWidth: '90vw', display: 'flex', flexDirection: 'column', gap: 16 }}
        onClick={e => e.stopPropagation()}
      >
        <div style={{ fontFamily: 'Montserrat,sans-serif', fontSize: 11, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.08em', color: 'var(--aj-text)' }}>
          {title}
        </div>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
          <label style={{ fontFamily: '"DM Sans",sans-serif', fontSize: 11, color: 'var(--aj-text-mid)' }}>Room Number <span style={{ fontStyle: 'italic', color: 'var(--aj-text-muted)' }}>(optional)</span></label>
          <input type="text" value={roomNumber} onChange={e => setRoomNumber(e.target.value)} placeholder="e.g. 101" style={inp} />
        </div>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
          <label style={{ fontFamily: '"DM Sans",sans-serif', fontSize: 11, color: 'var(--aj-text-mid)' }}>Room Template *</label>
          <RoomTemplatePicker templates={templates} value={roomTemplateId} onChange={handleTemplateChange} inputStyle={inp} />
        </div>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
          <div style={{ display: 'flex', alignItems: 'baseline', justifyContent: 'space-between' }}>
            <label style={{ fontFamily: '"DM Sans",sans-serif', fontSize: 11, color: 'var(--aj-text-mid)' }}>Room Name *</label>
            {roomTemplateId && (
              <button type="button" onClick={adoptTemplateName}
                style={{ fontFamily: '"DM Sans",sans-serif', fontSize: 10.5, color: 'var(--aj-accent)', background: 'none', border: 'none', cursor: 'pointer', padding: 0 }}>
                Use template name
              </button>
            )}
          </div>
          <input type="text" value={customName} onChange={e => setCustomName(e.target.value)} placeholder="e.g. Primary Bedroom" style={inp} />
        </div>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
          <label style={{ fontFamily: '"DM Sans",sans-serif', fontSize: 11, color: 'var(--aj-text-mid)' }}>Scope Type *</label>
          <select value={scopeType} onChange={e => setScopeType(e.target.value)} style={{ ...inp, background: 'var(--aj-bg-subtle)' }}>
            <option value="">Select scope…</option>
            {SCOPE_TYPE_OPTIONS.map(o => <option key={o} value={o}>{o}</option>)}
          </select>
          {scopeType && (
            <div style={{ fontFamily: '"DM Sans",sans-serif', fontSize: 11, color: 'var(--aj-text-muted)', lineHeight: 1.4 }}>{SCOPE_TYPE_DEFINITIONS[scopeType]}</div>
          )}
        </div>
        {error && (
          <div style={{ fontFamily: '"DM Sans",sans-serif', fontSize: 11.5, color: '#c0392b' }}>{error}</div>
        )}
        <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end', paddingTop: 8, borderTop: '0.5px solid var(--aj-border)', marginTop: 4 }}>
          <button onClick={onClose} style={{ fontFamily: '"DM Sans",sans-serif', fontSize: 12, padding: '7px 16px', border: '0.5px solid var(--aj-border-mid)', borderRadius: 4, background: 'transparent', color: 'var(--aj-text)', cursor: 'pointer' }}>Cancel</button>
          <button onClick={handleSave} disabled={disabled || saving} style={{ fontFamily: '"DM Sans",sans-serif', fontSize: 12, padding: '7px 16px', border: 'none', borderRadius: 4, background: '#07272D', color: '#fff', cursor: (disabled || saving) ? 'default' : 'pointer', opacity: (disabled || saving) ? 0.6 : 1 }}>
            {saving ? 'Saving…' : 'Save'}
          </button>
        </div>
      </div>
    </div>
  )
}

/* ── Per-field settings (the room's "settings" children) ─────────────────────────────────── */

// Order matches the Add/Edit form's field order. Room Name doubles as the room's own title —
// it's still listed here so it's independently editable, matching the other 3 settings.
const ROOM_FIELDS = [
  { key: 'Custom Name',   label: 'Room Name',     kind: 'text' },
  { key: 'Room Template', label: 'Room Template', kind: 'template' },
  { key: 'Scope Type',    label: 'Scope Type',    kind: 'select', options: SCOPE_TYPE_OPTIONS,
    hint: v => SCOPE_TYPE_DEFINITIONS[v] || 'What portion of a full rebuild of this room this work represents — drives its Budget Room Cost.' },
  { key: 'Room Number',   label: 'Room Number',   kind: 'text', optional: true },
]

// Room Area — two ways in, mirroring how the practice actually works: Length × Width is the
// conceptual estimate available from day one (even on a new build with no plan yet), Area Manual
// is the real number once a takeoff or as-built measurement exists. Airtable's Room Area Net
// formula already gives Area Manual priority when it's set (IF(NOT(Area Manual), Length*Width,
// Area Manual)) — this is just the missing UI to feed both inputs; no new calc logic here.
const ROOM_AREA_FIELDS = [
  { key: 'Length', label: 'Length', kind: 'number', unit: 'ft', optional: true },
  { key: 'Width',  label: 'Width',  kind: 'number', unit: 'ft', optional: true },
  {
    key: 'Area Manual', label: 'Manual Area Override', kind: 'number', unit: 'sf', optional: true,
    hint: 'Overrides Length × Width when set — use once a takeoff or as-built measurement exists. Leave blank to fall back to the conceptual Length × Width estimate.',
  },
]

function fieldSlug(key) { return key.toLowerCase().replace(/[^a-z0-9]+/g, '-') }

/** Single-field edit modal — the input control varies by `field.kind`; Save stays disabled
 *  until a required field has a value (Room Number is the only optional one). */
function RoomFieldEditModal({ field, value, templates, onSave, onClose }) {
  const [val, setVal] = useState(value)
  const [saving, setSaving] = useState(false)
  const [error, setError] = useState('')

  const inp = {
    fontFamily: '"DM Sans",sans-serif', fontSize: 12,
    padding: '8px 10px', border: '0.5px solid var(--aj-border-mid)',
    borderRadius: 4, background: 'var(--aj-bg-subtle)', color: 'var(--aj-text)',
    outline: 'none', boxSizing: 'border-box', width: '100%',
  }

  const invalid = !field.optional && !String(val || '').trim()

  async function handleSave() {
    if (invalid || saving) return
    setSaving(true)
    setError('')
    try {
      const payload = field.kind === 'template' ? [val]
        : field.kind === 'number' ? (val === '' || val == null ? null : Number(val))
        : String(val || '').trim()
      await onSave(payload)
      onClose()
    } catch (err) {
      setSaving(false)
      setError(err.message || 'Could not save this field.')
    }
  }

  return (
    <div style={{ position: 'fixed', inset: 0, zIndex: 900, background: 'rgba(0,0,0,0.35)', display: 'flex', alignItems: 'center', justifyContent: 'center' }} onClick={onClose}>
      <div style={{ background: 'var(--aj-bg-elevated)', borderRadius: 10, padding: '28px 32px', width: 360, maxWidth: '90vw', display: 'flex', flexDirection: 'column', gap: 16 }} onClick={e => e.stopPropagation()}>
        <div style={{ fontFamily: 'Montserrat,sans-serif', fontSize: 11, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.08em', color: 'var(--aj-text)' }}>
          Edit {field.label}
        </div>
        {field.kind === 'text' && (
          <input autoFocus type="text" value={val} onChange={e => setVal(e.target.value)} style={inp} />
        )}
        {field.kind === 'select' && (
          <select value={val} onChange={e => setVal(e.target.value)} style={{ ...inp, background: 'var(--aj-bg-subtle)' }}>
            <option value="">Select {field.label.toLowerCase()}…</option>
            {field.options.map(o => <option key={o} value={o}>{o}</option>)}
          </select>
        )}
        {field.kind === 'template' && (
          <RoomTemplatePicker templates={templates} value={val} onChange={setVal} inputStyle={inp} />
        )}
        {field.kind === 'number' && (
          <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
            <input
              autoFocus type="number" min={0}
              value={val ?? ''}
              onChange={e => setVal(e.target.value === '' ? null : Number(e.target.value))}
              style={inp}
            />
            {field.unit && <span style={{ fontFamily: '"DM Mono",monospace', fontSize: 11, color: 'var(--aj-text-mid)' }}>{field.unit}</span>}
          </div>
        )}
        {(() => {
          // Hint can be a fixed string (Area Manual) or a function of the current value
          // (Scope Type — the definition should update live as the picker changes).
          const hintText = typeof field.hint === 'function' ? field.hint(val) : field.hint
          return hintText ? <div style={{ fontFamily: '"DM Sans",sans-serif', fontSize: 11, color: 'var(--aj-text-muted)', lineHeight: 1.4 }}>{hintText}</div> : null
        })()}
        {error && <div style={{ fontFamily: '"DM Sans",sans-serif', fontSize: 11.5, color: '#c0392b' }}>{error}</div>}
        <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end', paddingTop: 8, borderTop: '0.5px solid var(--aj-border)', marginTop: 4 }}>
          <button onClick={onClose} style={{ fontFamily: '"DM Sans",sans-serif', fontSize: 12, padding: '7px 16px', border: '0.5px solid var(--aj-border-mid)', borderRadius: 4, background: 'transparent', color: 'var(--aj-text)', cursor: 'pointer' }}>Cancel</button>
          <button onClick={handleSave} disabled={invalid || saving} style={{ fontFamily: '"DM Sans",sans-serif', fontSize: 12, padding: '7px 16px', border: 'none', borderRadius: 4, background: '#07272D', color: '#fff', cursor: (invalid || saving) ? 'default' : 'pointer', opacity: (invalid || saving) ? 0.6 : 1 }}>
            {saving ? 'Saving…' : 'Save'}
          </button>
        </div>
      </div>
    </div>
  )
}

/** One setting row — leaf, no children, own ⋯ menu with a single "Edit" action. Never offers
 *  Delete (that's the room's own action, one level up). */
function RoomFieldRow({ field, room, templates, onSave }) {
  const menu = useActionMenu()
  const [editOpen, setEditOpen] = useState(false)
  const dataKey = 'room:' + room.id + ':field:' + fieldSlug(field.key)

  const rawValue = field.kind === 'template'
    ? ((room.fields['Room Template'] || [])[0] || '')
    : (room.fields[field.key] != null ? room.fields[field.key] : '')
  const isEmpty = rawValue === '' || rawValue == null
  const displayValue = field.kind === 'template' ? templateName(templates, rawValue)
    : field.kind === 'number' ? (isEmpty ? '' : rawValue + (field.unit ? ' ' + field.unit : ''))
    : rawValue

  async function handleFieldSave(payload) {
    await onSave(room.id, { [field.key]: payload })
  }

  return (
    <div className="wf-node wf-node--item" data-key={dataKey}>
      <div className="wf-row">
        <span className="wf-triangle-spacer wf-triangle-spacer--hidden" />
        <span className="wf-bullet wf-bullet--item" style={{ background: 'var(--wf-dot)' }} />
        <div className="wf-text-wrap">
          <span className="wf-node-text">{field.label}</span>
          <span style={{
            fontFamily: '"DM Mono",monospace', fontSize: 10, marginLeft: 8,
            color: isEmpty ? 'var(--aj-text-muted)' : 'var(--aj-text-mid)',
            fontStyle: isEmpty ? 'italic' : 'normal',
          }}>
            {isEmpty ? (field.optional ? 'Not set' : 'Required — not set') : displayValue}
          </span>
        </div>
        <ActionMenuButton menu={menu} />
      </div>
      <ActionMenu menu={menu}>
        <ActionMenuItem onClick={() => { menu.close(); setEditOpen(true) }}>Edit</ActionMenuItem>
      </ActionMenu>
      {editOpen && (
        <RoomFieldEditModal
          field={field}
          value={rawValue}
          templates={templates}
          onSave={handleFieldSave}
          onClose={() => setEditOpen(false)}
        />
      )}
    </div>
  )
}

/** Expandable "Room Area" branch — Length/Width (conceptual) + Manual Area Override (takeoff/
 *  as-built), grouped together with the live computed net SF as their shared display value.
 *  Room Area Net is an Airtable formula field (already recalculated server-side on save) — this
 *  never computes area itself, it only shows what Airtable already resolved. */
function RoomAreaCategoryRow({ room, onSave }) {
  const key = 'room:' + room.id + ':area'
  const isCollapsed = wf.collapsed[key] !== false
  const net = room.fields['Room Area Net']
  const hasNet = net != null && net > 0
  const manualSet = room.fields['Area Manual'] != null && room.fields['Area Manual'] !== ''
  return (
    <div className={`wf-node wf-node--cat${isCollapsed ? ' wf-node--collapsed' : ''}`} data-key={key}>
      <div className="wf-row">
        <button className="wf-triangle" data-fn="toggleCollapse" data-key={key}>
          <span className={`wf-tri-icon ${isCollapsed ? 'wf-tri-icon--right' : 'wf-tri-icon--down'}`} />
        </button>
        <span className="wf-bullet wf-bullet--cat" style={{ cursor: 'default' }} />
        <div className="wf-text-wrap">
          <span className="wf-node-text wf-node-text--cat">Room Area</span>
          <span style={{
            fontFamily: '"DM Mono",monospace', fontSize: 10, marginLeft: 8,
            color: hasNet ? 'var(--aj-text-mid)' : 'var(--aj-text-muted)',
            fontStyle: hasNet ? 'normal' : 'italic',
          }}>
            {hasNet ? Math.round(net).toLocaleString() + ' sf' + (manualSet ? ' (manual)' : '') : 'Not set'}
          </span>
        </div>
      </div>
      {!isCollapsed && (
        <div className="wf-children">
          {ROOM_AREA_FIELDS.map(f => <RoomFieldRow key={f.key} field={f} room={room} templates={null} onSave={onSave} />)}
        </div>
      )}
    </div>
  )
}

/** The room's settings, in order — the one canonical renderer for both the inline expanded
 *  tree row (below) and the room-detail zoom's content area (DesignSection.jsx). */
export function RoomFieldsList({ room, templates, onSave }) {
  return (
    <>
      {ROOM_FIELDS.map(f => <RoomFieldRow key={f.key} field={f} room={room} templates={templates} onSave={onSave} />)}
      <RoomAreaCategoryRow room={room} onSave={onSave} />
    </>
  )
}

/* ── The room row itself — expandable, shows RoomFieldsList as children ─────────────────── */

export function ConceptRoomRow({ room, levelId, templates, onSave, onDelete }) {
  const menu = useActionMenu()
  const [editOpen, setEditOpen] = useState(false)
  const name = roomDisplayName(room)
  const key = 'room:' + room.id
  const isCollapsed = wf.collapsed[key] !== false

  async function handleDelete() {
    menu.close()
    const ok = await confirmTypeDialog({
      title: 'Delete room?',
      message: `This will permanently delete "${name}". This cannot be undone.`,
    })
    if (!ok) return
    await onDelete(room.id)
  }

  return (
    <div className={`wf-node wf-node--cat${isCollapsed ? ' wf-node--collapsed' : ''}`} data-key={key}>
      <div className="wf-row">
        <button className="wf-triangle" data-fn="toggleCollapse" data-key={key}>
          <span className={`wf-tri-icon ${isCollapsed ? 'wf-tri-icon--right' : 'wf-tri-icon--down'}`} />
        </button>
        <button className="wf-bullet wf-bullet--cat" title="Focus on room"
          data-fn="zoomTo" data-level="room-detail" data-roomid={room.id} data-levelid={levelId} />
        <div className="wf-text-wrap">
          <span className="wf-node-text wf-node-text--cat">{name}</span>
        </div>
        <ActionMenuButton menu={menu} />
      </div>
      <ActionMenu menu={menu}>
        <ActionMenuItem onClick={() => { menu.close(); setEditOpen(true) }}>Edit</ActionMenuItem>
        <ActionMenuItem onClick={handleDelete} danger>Delete</ActionMenuItem>
      </ActionMenu>
      {!isCollapsed && (
        <div className="wf-children">
          <RoomFieldsList room={room} templates={templates} onSave={onSave} />
        </div>
      )}
      {editOpen && (
        <RoomFormModal
          title="Edit Room"
          initial={room.fields}
          onSave={fields => onSave(room.id, fields)}
          onClose={() => setEditOpen(false)}
        />
      )}
    </div>
  )
}

/** Self-fetching wrapper for the room-detail zoom page — cold-load safe (a direct/deep link
 *  may land here before any tree navigation has populated ctx.roomCache). Renders the SAME
 *  RoomFieldsList as the inline expanded row — editing here works identically; deleting the
 *  room lives on ZoomTitle's ⋯ menu (see ZoomTitle.jsx), matching every other entity-detail zoom. */
export function RoomDetailZoom({ roomId, levelId }) {
  const [room, setRoom] = useState(() => (ctx.roomCache?.[levelId] || []).find(r => r.id === roomId) || null)
  const loadedRef = useRef(false)
  const templates = useRoomTemplates()

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => {
    if (room || loadedRef.current) return
    loadedRef.current = true
    getLevelRooms(levelId)
      .then(records => {
        const r = records || []
        ctx.roomCache[levelId] = r
        setRoom(r.find(x => x.id === roomId) || null)
        ctx.render()
      })
      .catch(() => {})
  }, [roomId, levelId])

  async function handleSave(id, fields) {
    const updated = await updateRoom(id, fields)
    setRoom(prev => (prev && prev.id === id) ? { ...prev, fields: updated?.fields || { ...prev.fields, ...fields } } : prev)
    if (ctx.roomCache?.[levelId]) {
      ctx.roomCache[levelId] = ctx.roomCache[levelId].map(r => r.id === id ? { ...r, fields: updated?.fields || { ...r.fields, ...fields } } : r)
    }
    ctx.render() // title/breadcrumb read ctx.roomCache directly — refresh them if the name changed
  }

  if (!room) return <div className="wf-loading">Loading…</div>
  return (
    <div className="wf-list">
      <RoomFieldsList room={room} templates={templates} onSave={handleSave} />
    </div>
  )
}

function emptyRow(message) {
  return (
    <div className="wf-node wf-node--item">
      <div className="wf-row">
        <span className="wf-triangle-spacer wf-triangle-spacer--hidden" />
        <span className="wf-bullet wf-bullet--item" style={{ opacity: 0 }} />
        <div className="wf-text-wrap">
          <span className="wf-node-text" style={{ color: 'var(--aj-text-muted)', fontStyle: 'italic' }}>{message}</span>
        </div>
      </div>
    </div>
  )
}

/**
 * Self-contained "Rooms" category node for a Concept Level. wf.collapsed key
 * is scoped by levelId so tree state persists like every other collapsible node.
 */
export default function ConceptRoomsSection({ levelId, conceptId, levelName, zoomMode }) {
  const [rooms, setRooms] = useState(null)
  const loadedRef = useRef(false)
  const [addOpen, setAddOpen] = useState(false)
  const menu = useActionMenu()
  const templates = useRoomTemplates()
  const key = 'sec:level:' + levelId + ':rooms'
  const isCollapsed = zoomMode ? false : (wf.collapsed[key] !== false)

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => {
    if (!levelId || loadedRef.current) return
    loadedRef.current = true
    getLevelRooms(levelId)
      .then(records => { const r = records || []; setRooms(r); ctx.roomCache[levelId] = r; ctx.render() })
      .catch(() => setRooms([]))
  }, [levelId])

  // zoomMode: "Add Room" lives in ZoomTitle's ⋯ menu — bridged via CustomEvent.
  useEffect(() => {
    if (!zoomMode) return
    function onOpenAdd(e) { if (e.detail?.levelId === levelId) setAddOpen(true) }
    window.addEventListener('open-add-room', onOpenAdd)
    return () => window.removeEventListener('open-add-room', onOpenAdd)
  }, [zoomMode, levelId])

  async function handleAdd(fields) {
    await createRoom(levelId, conceptId, levelName, fields)
    const records = await getLevelRooms(levelId)
    setRooms(records || [])
    ctx.roomCache[levelId] = records || []
    ctx.render()
  }

  async function handleSaveRoom(roomId, fields) {
    // Use the server's returned record (not a manual field merge) — Airtable recalculates
    // formula fields like Room Area Net on every PATCH, and a manual merge would leave the
    // "Room Area" display stale until the next full reload.
    const updated = await updateRoom(roomId, fields)
    setRooms(prev => {
      const next = (prev || []).map(r => r.id === roomId ? { ...r, fields: updated?.fields || { ...r.fields, ...fields } } : r)
      ctx.roomCache[levelId] = next
      return next
    })
  }

  async function handleDeleteRoom(roomId) {
    await deleteRoom(roomId)
    setRooms(prev => {
      const next = (prev || []).filter(r => r.id !== roomId)
      ctx.roomCache[levelId] = next
      return next
    })
    ctx.render()
  }

  const count = Array.isArray(rooms) ? rooms.length : null

  const content = (
    <>
      {!rooms && <div className="wf-loading">Loading…</div>}
      {Array.isArray(rooms) && rooms.length === 0 && emptyRow('No rooms yet')}
      {Array.isArray(rooms) && rooms.map(room => (
        <ConceptRoomRow key={room.id} room={room} levelId={levelId} templates={templates} onSave={handleSaveRoom} onDelete={handleDeleteRoom} />
      ))}
    </>
  )

  const addModal = addOpen && (
    <RoomFormModal
      title="Add Room"
      initial={null}
      onSave={handleAdd}
      onClose={() => setAddOpen(false)}
    />
  )

  if (zoomMode) {
    return (
      <div className="wf-list">
        {content}
        {addModal}
      </div>
    )
  }

  return (
    <div className={`wf-node wf-node--cat${isCollapsed ? ' wf-node--collapsed' : ''}`} data-key={key}>
      <div className="wf-row">
        <button className="wf-triangle" data-fn="toggleCollapse" data-key={key}>
          <span className={`wf-tri-icon ${isCollapsed ? 'wf-tri-icon--right' : 'wf-tri-icon--down'}`} />
        </button>
        <button className="wf-bullet wf-bullet--cat" title="Focus on Rooms"
          data-fn="zoomTo" data-level="level-rooms" data-levelid={levelId} data-conceptid={conceptId} />
        <div className="wf-text-wrap">
          <span className="wf-node-text wf-node-text--cat">Rooms</span>
          {count != null && <span className="wf-prog" style={{ color: 'var(--aj-text-muted)', marginLeft: 8 }}>{count}</span>}
        </div>
        <ActionMenuButton menu={menu} />
      </div>
      <ActionMenu menu={menu}>
        <ActionMenuItem onClick={() => { menu.close(); setAddOpen(true) }}>Add Room</ActionMenuItem>
      </ActionMenu>
      {!isCollapsed && <div className="wf-children">{content}</div>}
      {addModal}
    </div>
  )
}
